import { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { sankey as d3Sankey, sankeyLinkHorizontal } from 'd3-sankey';
import { crmService } from '../../services/crmService';

// ─── Visual constants ──────────────────────────────────────────────────────────

const ALIGNMENT_COLOR = {
  five_eyes:  '#22D3EE',
  nato_ally:  '#3B82F6',
  eu_partner: '#06B6D4',
  friendly:   '#10B981',
  neutral:    '#6B7280',
  unfriendly: '#F59E0B',
  adversary:  '#EF4444',
};

const COA_COLOR = {
  ma:          '#7C3AED',
  personnel:   '#3B82F6',
  capital:     '#10B981',
  operational: '#F59E0B',
};

const COA_ICON = { ma: 'M&A', personnel: '👥', capital: '💰', operational: '⚙' };

const AVAIL_META = {
  available:         { label: 'Available',         color: '#10B981' },
  constrained:       { label: 'Constrained',        color: '#F59E0B' },
  critical_shortage: { label: 'Critical Shortage',  color: '#EF4444' },
  unavailable:       { label: 'Unavailable',        color: '#6B7280' },
};

const ALIGNMENT_FILTERS = ['adversary','unfriendly','neutral','friendly','nato_ally','eu_partner','five_eyes'];

// ─── Sankey node helpers ───────────────────────────────────────────────────────

function nodeColor(d) {
  if (d.type === 'component')  return ALIGNMENT_COLOR[d.alignment] || '#6B7280';
  if (d.type === 'growth_coa') return COA_COLOR[d.action_type] || '#7C3AED';
  return '#1E2D45';
}

function nodeBorderColor(d) {
  if (d.type === 'component') {
    if (d.availability === 'critical_shortage') return '#EF4444';
    if (d.availability === 'constrained')       return '#F59E0B';
    return 'none';
  }
  if (d.type === 'equipment')  return '#22D3EE';
  if (d.type === 'capability') return '#3B82F6';
  if (d.type === 'vertical')   return d.has_coa ? '#22D3EE' : '#EF4444';
  if (d.type === 'growth_coa') return COA_COLOR[d.action_type] || '#7C3AED';
  return '#374151';
}

const COLS = [
  { label: 'COMPONENTS' }, { label: 'EQUIPMENT' }, { label: 'CAPABILITIES' },
  { label: 'VERTICALS' },  { label: 'COURSES OF ACTION' },
];

// ─── Shared UI primitives ──────────────────────────────────────────────────────

function AlignmentBadge({ alignment }) {
  const color = ALIGNMENT_COLOR[alignment] || '#6B7280';
  const dark = ['five_eyes','neutral','eu_partner'].includes(alignment);
  return (
    <span style={{
      background: color, color: dark ? '#0A0F1C' : '#fff',
      borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700,
      display: 'inline-block', whiteSpace: 'nowrap',
    }}>
      {(alignment || 'unknown').replace(/_/g, ' ').toUpperCase()}
    </span>
  );
}

function AvailBadge({ status }) {
  const m = AVAIL_META[status] || { label: status || '—', color: '#6B7280' };
  return (
    <span style={{
      background: m.color + '22', color: m.color,
      border: `1px solid ${m.color}`, borderRadius: 4,
      padding: '2px 7px', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {m.label}
    </span>
  );
}

function RiskBar({ score }) {
  const pct = (score || 0) * 10;
  const color = pct >= 80 ? '#EF4444' : pct >= 50 ? '#F59E0B' : '#10B981';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 56, background: '#1E2D45', borderRadius: 3, height: 5, flexShrink: 0 }}>
        <div style={{ width: `${pct}%`, background: color, height: 5, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 24 }}>{score || 0}</span>
    </div>
  );
}

function Modal({ title, onClose, children, width }) {
  const w = Math.min(width || 580, (typeof window !== 'undefined' ? window.innerWidth : 800) - 40);
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#111827', border: '1px solid #1E2D45', borderRadius: 10, width: w, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1E2D45', flexShrink: 0 }}>
          <span style={{ fontWeight: 700, color: '#F1F5F9', fontSize: 14 }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, color: '#94A3B8', fontWeight: 600, marginBottom: 4, letterSpacing: '0.05em' }}>
        {label.toUpperCase()}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inp = { width: '100%', background: '#0A0F1C', border: '1px solid #1E2D45', borderRadius: 6, padding: '7px 10px', color: '#E2E8F0', fontSize: 13, boxSizing: 'border-box', outline: 'none' };
const td  = { padding: '10px 10px', verticalAlign: 'middle' };
const th  = { padding: '8px 10px', textAlign: 'left', color: '#64748B', fontWeight: 600, fontSize: 10, letterSpacing: '0.05em', whiteSpace: 'nowrap' };

const btnAdd    = { background: '#0D1F3C', color: '#22D3EE', border: '1px solid #22D3EE44', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 };
const btnIcon   = { background: 'transparent', color: '#94A3B8', border: '1px solid #374151', borderRadius: 4, padding: '4px 9px', cursor: 'pointer', fontSize: 11 };
const btnCancel = { background: 'transparent', color: '#94A3B8', border: '1px solid #374151', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 12 };
const btnSave   = { background: '#22D3EE', color: '#0A0F1C', border: 'none', borderRadius: 6, padding: '7px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 700 };

// ─── COMPONENTS TAB ────────────────────────────────────────────────────────────

function ComponentModal({ component, onClose, onSave }) {
  const isEdit = !!component;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [verticals, setVerticals] = useState([]);
  const [selectedVerts, setSelectedVerts] = useState(
    new Set((component?.vertical_ids || []).filter(Boolean).map(Number))
  );
  const [form, setForm] = useState({
    name:                  component?.name || '',
    part_number:           component?.part_number || '',
    category:              component?.category || '',
    manufacturer_name:     component?.manufacturer_name || '',
    manufacturer_country:  component?.manufacturer_country || '',
    manufacturer_alignment:component?.manufacturer_alignment || '',
    tactical_use_cases:    (component?.tactical_use_cases || []).join(', '),
    availability_status:   component?.availability_status || 'available',
    lead_time_weeks:       component?.lead_time_weeks || '',
    risk_score:            component?.risk_score || 5,
    risk_notes:            component?.risk_notes || '',
    alternative_sources:   (component?.alternative_sources?.vendors || []).join(', '),
    website:               component?.website || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    crmService.getVerticals().then(setVerticals).catch(console.error);
  }, []);

  const toggleVert = id => setSelectedVerts(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      const payload = {
        ...form,
        lead_time_weeks: form.lead_time_weeks ? parseInt(form.lead_time_weeks) : null,
        risk_score: parseInt(form.risk_score),
        tactical_use_cases: form.tactical_use_cases
          ? form.tactical_use_cases.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        alternative_sources: form.alternative_sources
          ? { vendors: form.alternative_sources.split(',').map(s => s.trim()).filter(Boolean) }
          : null,
      };
      const saved = isEdit
        ? await crmService.updateSupplyChainComponent(component.id, payload)
        : await crmService.createSupplyChainComponent(payload);

      const prevVerts = new Set((component?.vertical_ids || []).filter(Boolean).map(Number));
      const toAdd    = [...selectedVerts].filter(v => !prevVerts.has(v));
      const toRemove = [...prevVerts].filter(v => !selectedVerts.has(v));
      await Promise.all([
        ...toAdd.map(vid    => crmService.addComponentVertical(saved.id, vid)),
        ...toRemove.map(vid => crmService.removeComponentVertical(saved.id, vid)),
      ]);
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? `Edit: ${component.name}` : 'Add Component'} onClose={onClose} width={640}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 16px' }}>
        <Field label="Name" required><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} /></Field>
        <Field label="Part Number"><input style={inp} value={form.part_number} onChange={e => set('part_number', e.target.value)} /></Field>
        <Field label="Category">
          <select style={inp} value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="">— select —</option>
            {['semiconductor','sensor','actuator','software','material','subsystem','platform','other'].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Availability">
          <select style={inp} value={form.availability_status} onChange={e => set('availability_status', e.target.value)}>
            {['available','constrained','critical_shortage','unavailable'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
          </select>
        </Field>
        <Field label="Manufacturer"><input style={inp} value={form.manufacturer_name} onChange={e => set('manufacturer_name', e.target.value)} /></Field>
        <Field label="Country"><input style={inp} value={form.manufacturer_country} onChange={e => set('manufacturer_country', e.target.value)} /></Field>
        <Field label="Alignment">
          <select style={inp} value={form.manufacturer_alignment} onChange={e => set('manufacturer_alignment', e.target.value)}>
            <option value="">— select —</option>
            {['five_eyes','nato_ally','eu_partner','friendly','neutral','unfriendly','adversary'].map(a => <option key={a} value={a}>{a.replace(/_/g,' ')}</option>)}
          </select>
        </Field>
        <Field label="Lead Time (weeks)"><input style={inp} type="number" min="0" value={form.lead_time_weeks} onChange={e => set('lead_time_weeks', e.target.value)} /></Field>
      </div>

      <Field label={`Risk Score: ${form.risk_score}/10`}>
        <input type="range" min="1" max="10" value={form.risk_score} onChange={e => set('risk_score', e.target.value)} style={{ width: '100%', accentColor: '#EF4444' }} />
      </Field>
      <Field label="Tactical Use Cases (comma-separated)">
        <textarea style={{ ...inp, height: 52, resize: 'vertical' }} value={form.tactical_use_cases} onChange={e => set('tactical_use_cases', e.target.value)} placeholder="drone flight control, radar signal processing, …" />
      </Field>
      <Field label="Risk Notes">
        <textarea style={{ ...inp, height: 66, resize: 'vertical' }} value={form.risk_notes} onChange={e => set('risk_notes', e.target.value)} />
      </Field>
      <Field label="Alternative Sources (comma-separated)">
        <textarea style={{ ...inp, height: 52, resize: 'vertical' }} value={form.alternative_sources} onChange={e => set('alternative_sources', e.target.value)} placeholder="NXP i.MX RT1060, Intel Foundry 18A, …" />
      </Field>
      <Field label="Website"><input style={inp} type="url" value={form.website} onChange={e => set('website', e.target.value)} /></Field>

      {verticals.length > 0 && (
        <Field label="Verticals">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5px 12px' }}>
            {verticals.map(v => (
              <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: selectedVerts.has(v.id) ? '#22D3EE' : '#94A3B8' }}>
                <input type="checkbox" checked={selectedVerts.has(v.id)} onChange={() => toggleVert(v.id)} style={{ accentColor: '#22D3EE' }} />
                {v.name}
              </label>
            ))}
          </div>
        </Field>
      )}

      {error && <div style={{ color: '#EF4444', fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
        <button onClick={onClose} style={btnCancel}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ ...btnSave, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Component')}
        </button>
      </div>
    </Modal>
  );
}

function ComponentsTab({ onRefreshSankey }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await crmService.getSupplyChainComponents()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (confirmDel !== id) { setConfirmDel(id); return; }
    try { await crmService.deleteSupplyChainComponent(id); setConfirmDel(null); await load(); onRefreshSankey(); }
    catch (e) { alert(e.message); }
  };

  const afterSave = async () => { setModalOpen(false); setEditTarget(null); await load(); onRefreshSankey(); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button onClick={() => { setEditTarget(null); setModalOpen(true); }} style={btnAdd}>+ Add Component</button>
      </div>
      {loading ? <div style={{ color: '#64748B', padding: 20 }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1E2D45' }}>
                {['Risk','Name','Part #','Category','Manufacturer','Alignment','Availability','Lead Time','Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #0D1117' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#0D1F3C22'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={td}><RiskBar score={r.risk_score} /></td>
                  <td style={{ ...td, fontWeight: 600, color: '#E2E8F0', maxWidth: 160 }}>{r.name}</td>
                  <td style={{ ...td, color: '#64748B', fontSize: 11 }}>{r.part_number || '—'}</td>
                  <td style={{ ...td, color: '#94A3B8' }}>{r.category || '—'}</td>
                  <td style={{ ...td, color: '#94A3B8' }}>
                    {r.manufacturer_name && <div>{r.manufacturer_name}</div>}
                    {r.manufacturer_country && <div style={{ fontSize: 10, color: '#64748B' }}>{r.manufacturer_country}</div>}
                  </td>
                  <td style={td}><AlignmentBadge alignment={r.manufacturer_alignment} /></td>
                  <td style={td}><AvailBadge status={r.availability_status} /></td>
                  <td style={{ ...td, color: '#94A3B8' }}>{r.lead_time_weeks ? `${r.lead_time_weeks}w` : '—'}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => { setEditTarget(r); setModalOpen(true); }} style={btnIcon}>Edit</button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        onBlur={() => setTimeout(() => setConfirmDel(null), 200)}
                        style={{ ...btnIcon, background: confirmDel === r.id ? '#EF444422' : 'transparent', color: confirmDel === r.id ? '#EF4444' : '#94A3B8', borderColor: confirmDel === r.id ? '#EF4444' : '#374151' }}
                      >
                        {confirmDel === r.id ? 'Confirm?' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, color: '#64748B', textAlign: 'center' }}>No components. Add one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {modalOpen && (
        <ComponentModal component={editTarget} onClose={() => { setModalOpen(false); setEditTarget(null); }} onSave={afterSave} />
      )}
    </div>
  );
}

// ─── EQUIPMENT TAB ─────────────────────────────────────────────────────────────

function EquipmentModal({ equipment, onClose, onSave }) {
  const isEdit = !!equipment;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [form, setForm] = useState({
    name:        equipment?.name || '',
    designation: equipment?.designation || '',
    category:    equipment?.category || '',
    description: equipment?.description || '',
    primary_user:equipment?.primary_user || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      isEdit
        ? await crmService.updateMilitaryEquipment(equipment.id, form)
        : await crmService.createMilitaryEquipment(form);
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? `Edit: ${equipment.name}` : 'Add Equipment'} onClose={onClose} width={520}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 16px' }}>
        <Field label="Name" required><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} /></Field>
        <Field label="Designation"><input style={inp} value={form.designation} onChange={e => set('designation', e.target.value)} /></Field>
        <Field label="Category">
          <select style={inp} value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="">— select —</option>
            {['aircraft','uav','ground_vehicle','naval_vessel','missile','munition','sensor','c2_system','communications','satellite','other'].map(c => (
              <option key={c} value={c}>{c.replace(/_/g,' ')}</option>
            ))}
          </select>
        </Field>
        <Field label="Primary User"><input style={inp} value={form.primary_user} onChange={e => set('primary_user', e.target.value)} /></Field>
      </div>
      <Field label="Description">
        <textarea style={{ ...inp, height: 72, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} />
      </Field>
      {error && <div style={{ color: '#EF4444', fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={btnCancel}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ ...btnSave, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Equipment')}
        </button>
      </div>
    </Modal>
  );
}

function LinkComponentsModal({ equipment, onClose, onSave }) {
  const [allComponents, setAllComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentIds = new Set((equipment.component_ids || []).filter(Boolean).map(Number));
  const [selected, setSelected]     = useState(new Set(currentIds));
  const [criticality, setCriticality] = useState(() => {
    const init = {};
    [...currentIds].forEach(cid => { init[cid] = 5; });
    return init;
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    crmService.getSupplyChainComponents().then(c => { setAllComponents(c); setLoading(false); }).catch(console.error);
  }, []);

  const toggle = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSave = async () => {
    setSaving(true);
    try {
      const toAdd    = [...selected].filter(id => !currentIds.has(id));
      const toRemove = [...currentIds].filter(id => !selected.has(id));
      const toUpdate = [...selected].filter(id => currentIds.has(id));
      await Promise.all([
        ...toAdd.map(cid    => crmService.linkEquipmentComponent(equipment.id, cid, criticality[cid] || 5)),
        ...toRemove.map(cid => crmService.unlinkEquipmentComponent(equipment.id, cid)),
        ...toUpdate.map(cid => crmService.linkEquipmentComponent(equipment.id, cid, criticality[cid] || 5)),
      ]);
      onSave();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Link Components → ${equipment.name}`} onClose={onClose} width={540}>
      {loading ? <div style={{ color: '#64748B' }}>Loading components…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {allComponents.map(c => (
            <div key={c.id} style={{ background: selected.has(c.id) ? '#0D1F3C' : '#0A0F1C', border: `1px solid ${selected.has(c.id) ? '#22D3EE44' : '#1E2D45'}`, borderRadius: 6, padding: '8px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} style={{ accentColor: '#22D3EE', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#E2E8F0' }}>{c.name}</div>
                  <div style={{ fontSize: 10, color: '#64748B' }}>
                    {c.manufacturer_name} · <AlignmentBadge alignment={c.manufacturer_alignment} />
                  </div>
                </div>
                <RiskBar score={c.risk_score} />
              </div>
              {selected.has(c.id) && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #1E2D45' }}>
                  <label style={{ fontSize: 11, color: '#94A3B8' }}>Criticality: {criticality[c.id] || 5}/10</label>
                  <input type="range" min="1" max="10" value={criticality[c.id] || 5}
                    onChange={e => setCriticality(p => ({ ...p, [c.id]: parseInt(e.target.value) }))}
                    style={{ width: '100%', accentColor: '#22D3EE', marginTop: 4 }} />
                </div>
              )}
            </div>
          ))}
          {allComponents.length === 0 && <div style={{ color: '#64748B', padding: 12 }}>No components yet.</div>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} style={btnCancel}>Cancel</button>
        <button onClick={handleSave} disabled={saving || loading} style={{ ...btnSave, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Links'}
        </button>
      </div>
    </Modal>
  );
}

function EquipmentTab({ onRefreshSankey }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await crmService.getMilitaryEquipment()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const afterSave = async () => { setModalOpen(false); setEditTarget(null); await load(); onRefreshSankey(); };
  const afterLink = async () => { setLinkTarget(null); await load(); onRefreshSankey(); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button onClick={() => { setEditTarget(null); setModalOpen(true); }} style={btnAdd}>+ Add Equipment</button>
      </div>
      {loading ? <div style={{ color: '#64748B', padding: 20 }}>Loading…</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1E2D45' }}>
              {['Name','Designation','Category','Primary User','Linked Components','Actions'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid #0D1117' }}
                onMouseEnter={e => e.currentTarget.style.background = '#0D1F3C22'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ ...td, fontWeight: 600, color: '#E2E8F0' }}>{r.name}</td>
                <td style={{ ...td, color: '#22D3EE', fontSize: 11 }}>{r.designation || '—'}</td>
                <td style={{ ...td, color: '#94A3B8' }}>{r.category ? r.category.replace(/_/g,' ') : '—'}</td>
                <td style={{ ...td, color: '#94A3B8', maxWidth: 180 }}>{r.primary_user || '—'}</td>
                <td style={{ ...td, color: '#64748B' }}>{(r.component_ids || []).length} linked</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => { setEditTarget(r); setModalOpen(true); }} style={btnIcon}>Edit</button>
                    <button onClick={() => setLinkTarget(r)} style={{ ...btnIcon, color: '#22D3EE', borderColor: '#22D3EE44' }}>Link Components</button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} style={{ padding: 24, color: '#64748B', textAlign: 'center' }}>No equipment. Add one above.</td></tr>}
          </tbody>
        </table>
      )}
      {modalOpen && <EquipmentModal equipment={editTarget} onClose={() => { setModalOpen(false); setEditTarget(null); }} onSave={afterSave} />}
      {linkTarget && <LinkComponentsModal equipment={linkTarget} onClose={() => setLinkTarget(null)} onSave={afterLink} />}
    </div>
  );
}

// ─── CAPABILITIES TAB ──────────────────────────────────────────────────────────

function CapabilityModal({ capability, onClose, onSave }) {
  const isEdit = !!capability;
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const [form, setForm] = useState({ name: capability?.name || '', description: capability?.description || '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      isEdit
        ? await crmService.updateMilitaryCapability(capability.id, form)
        : await crmService.createMilitaryCapability(form);
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? `Edit: ${capability.name}` : 'Add Capability'} onClose={onClose} width={480}>
      <Field label="Name" required><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} /></Field>
      <Field label="Description">
        <textarea style={{ ...inp, height: 90, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} />
      </Field>
      {error && <div style={{ color: '#EF4444', fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={btnCancel}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ ...btnSave, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Capability')}
        </button>
      </div>
    </Modal>
  );
}

function LinkEquipmentModal({ capability, onClose, onSave }) {
  const [allEquipment, setAllEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentIds = new Set((capability.equipment_ids || []).filter(Boolean).map(Number));
  const [selected, setSelected] = useState(new Set(currentIds));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    crmService.getMilitaryEquipment().then(e => { setAllEquipment(e); setLoading(false); }).catch(console.error);
  }, []);

  const toggle = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSave = async () => {
    setSaving(true);
    try {
      const toAdd    = [...selected].filter(id => !currentIds.has(id));
      const toRemove = [...currentIds].filter(id => !selected.has(id));
      await Promise.all([
        ...toAdd.map(eid    => crmService.linkCapabilityEquipment(capability.id, eid)),
        ...toRemove.map(eid => crmService.unlinkCapabilityEquipment(capability.id, eid)),
      ]);
      onSave();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Link Equipment → ${capability.name}`} onClose={onClose} width={460}>
      {loading ? <div style={{ color: '#64748B' }}>Loading equipment…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {allEquipment.map(e => (
            <label key={e.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 12px', borderRadius: 6,
              background: selected.has(e.id) ? '#0D1F3C' : '#0A0F1C',
              border: `1px solid ${selected.has(e.id) ? '#3B82F644' : '#1E2D45'}`,
            }}>
              <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} style={{ accentColor: '#3B82F6', flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#E2E8F0' }}>{e.name}</div>
                {e.designation && <div style={{ fontSize: 10, color: '#64748B' }}>{e.designation}</div>}
              </div>
            </label>
          ))}
          {allEquipment.length === 0 && <div style={{ color: '#64748B', padding: 12 }}>No equipment yet.</div>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} style={btnCancel}>Cancel</button>
        <button onClick={handleSave} disabled={saving || loading} style={{ ...btnSave, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving…' : 'Save Links'}
        </button>
      </div>
    </Modal>
  );
}

function CapabilitiesTab({ onRefreshSankey }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await crmService.getMilitaryCapabilities()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const afterSave = async () => { setModalOpen(false); setEditTarget(null); await load(); onRefreshSankey(); };
  const afterLink = async () => { setLinkTarget(null); await load(); onRefreshSankey(); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button onClick={() => { setEditTarget(null); setModalOpen(true); }} style={btnAdd}>+ Add Capability</button>
      </div>
      {loading ? <div style={{ color: '#64748B', padding: 20 }}>Loading…</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #1E2D45' }}>
              {['Name','Description','Linked Equipment','Actions'].map(h => <th key={h} style={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid #0D1117' }}
                onMouseEnter={e => e.currentTarget.style.background = '#0D1F3C22'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ ...td, fontWeight: 600, color: '#E2E8F0', whiteSpace: 'nowrap' }}>{r.name}</td>
                <td style={{ ...td, color: '#94A3B8', fontSize: 11, maxWidth: 320 }}>{r.description || '—'}</td>
                <td style={{ ...td, color: '#64748B' }}>{(r.equipment_ids || []).length} linked</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => { setEditTarget(r); setModalOpen(true); }} style={btnIcon}>Edit</button>
                    <button onClick={() => setLinkTarget(r)} style={{ ...btnIcon, color: '#3B82F6', borderColor: '#3B82F644' }}>Link Equipment</button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} style={{ padding: 24, color: '#64748B', textAlign: 'center' }}>No capabilities. Add one above.</td></tr>}
          </tbody>
        </table>
      )}
      {modalOpen && <CapabilityModal capability={editTarget} onClose={() => { setModalOpen(false); setEditTarget(null); }} onSave={afterSave} />}
      {linkTarget && <LinkEquipmentModal capability={linkTarget} onClose={() => setLinkTarget(null)} onSave={afterLink} />}
    </div>
  );
}

// ─── MANAGE PANEL ──────────────────────────────────────────────────────────────

function ManagePanel({ onRefreshSankey }) {
  const [tab, setTab] = useState('components');
  const TABS = [['components','Components'],['equipment','Equipment'],['capabilities','Capabilities']];
  return (
    <div>
      <div style={{ display: 'flex', borderBottom: '1px solid #1E2D45', marginBottom: 20 }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '8px 20px', background: 'none', border: 'none',
            borderBottom: tab === k ? '2px solid #22D3EE' : '2px solid transparent',
            color: tab === k ? '#22D3EE' : '#6B7280',
            cursor: 'pointer', fontSize: 13, fontWeight: tab === k ? 600 : 400,
            marginBottom: -1,
          }}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'components'  && <ComponentsTab  onRefreshSankey={onRefreshSankey} />}
      {tab === 'equipment'   && <EquipmentTab   onRefreshSankey={onRefreshSankey} />}
      {tab === 'capabilities'&& <CapabilitiesTab onRefreshSankey={onRefreshSankey} />}
    </div>
  );
}

// ─── SANKEY TOOLTIP ────────────────────────────────────────────────────────────

function SankeyTooltip({ node, pos }) {
  if (!node || !pos) return null;
  const content = (() => {
    if (node.type === 'component') return (
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{node.name}</div>
        <div style={{ color: '#94A3B8', fontSize: 11, marginBottom: 8 }}>{node.manufacturer} · {node.manufacturer_country}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <AlignmentBadge alignment={node.alignment} />
          <AvailBadge status={node.availability} />
        </div>
        <div style={{ marginBottom: 6 }}>
          <div style={{ color: '#94A3B8', fontSize: 10, marginBottom: 2 }}>RISK SCORE</div>
          <RiskBar score={node.risk_score} />
        </div>
        {node.risk_notes && (
          <div style={{ fontSize: 11, color: '#94A3B8', borderTop: '1px solid #1E2D45', paddingTop: 6, marginTop: 6 }}>{node.risk_notes}</div>
        )}
        {node.alternatives?.vendors?.length > 0 && (
          <div style={{ borderTop: '1px solid #1E2D45', paddingTop: 6, marginTop: 6 }}>
            <div style={{ color: '#94A3B8', fontSize: 10, marginBottom: 4 }}>ALTERNATIVE SOURCES</div>
            {node.alternatives.vendors.map((v, i) => <div key={i} style={{ fontSize: 11, color: '#CBD5E1' }}>• {v}</div>)}
          </div>
        )}
      </div>
    );
    if (node.type === 'equipment') return (
      <div>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{node.name}</div>
        {node.designation && <div style={{ color: '#22D3EE', fontSize: 11, marginBottom: 4 }}>{node.designation}</div>}
        {node.primary_user && <div style={{ color: '#94A3B8', fontSize: 11 }}>Users: {node.primary_user}</div>}
      </div>
    );
    if (node.type === 'capability') return (
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{node.name}</div>
        {node.description && <div style={{ fontSize: 11, color: '#94A3B8' }}>{node.description}</div>}
      </div>
    );
    if (node.type === 'vertical') return (
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{node.name}</div>
        {!node.has_coa && <div style={{ color: '#EF4444', fontSize: 11, marginBottom: 6 }}>⚠ No active Course of Action</div>}
        {node.customer_problem && <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 4 }}><span style={{ color: '#CBD5E1' }}>Problem: </span>{node.customer_problem}</div>}
        {node.key_buyers && <div style={{ fontSize: 11, color: '#94A3B8' }}><span style={{ color: '#CBD5E1' }}>Buyers: </span>{node.key_buyers}</div>}
      </div>
    );
    if (node.type === 'growth_coa') return (
      <div>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{node.name}</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ background: COA_COLOR[node.action_type] || '#7C3AED', color: '#fff', borderRadius: 4, padding: '2px 6px', fontSize: 10 }}>
            {COA_ICON[node.action_type]} {(node.action_type || '').toUpperCase()}
          </span>
          <span style={{ background: '#1E2D45', color: '#94A3B8', borderRadius: 4, padding: '2px 6px', fontSize: 10 }}>
            {(node.stage || '').replace('_',' ')}
          </span>
        </div>
        {node.estimated_value && <div style={{ fontSize: 11, color: '#10B981' }}>${(node.estimated_value / 1e6).toFixed(1)}M est.</div>}
        {node.priority && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Priority {node.priority}</div>}
      </div>
    );
    return <div>{node.name}</div>;
  })();

  return (
    <div style={{
      position: 'fixed',
      left: Math.min(pos.x + 12, window.innerWidth - 280),
      top:  Math.min(pos.y - 10, window.innerHeight - 320),
      width: 260, background: '#111827', border: '1px solid #1E2D45',
      borderRadius: 8, padding: 12, fontSize: 12, color: '#E2E8F0',
      zIndex: 1000, pointerEvents: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    }}>
      {content}
    </div>
  );
}

// ─── SANKEY VIEW ───────────────────────────────────────────────────────────────

function SankeyView({ data, loading, error }) {
  const svgRef       = useRef(null);
  const containerRef = useRef(null);
  const [simulateMode, setSimulateMode] = useState(false);
  const [selectedComp, setSelectedComp] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [activeAlignments, setActiveAlignments] = useState(
    new Set(['adversary','unfriendly','neutral','friendly','nato_ally','eu_partner','five_eyes'])
  );

  const toggleAlignment = a => {
    setActiveAlignments(prev => { const n = new Set(prev); n.has(a) ? n.delete(a) : n.add(a); return n; });
    setSelectedComp(null);
  };

  const filteredData = useCallback(() => {
    const hidden = new Set(
      data.nodes.filter(n => n.type === 'component' && !activeAlignments.has(n.alignment)).map(n => n.id)
    );
    const links = data.links.filter(l => !hidden.has(l.source) && !hidden.has(l.target));
    const reachable = new Set();
    links.forEach(l => { reachable.add(l.source); reachable.add(l.target); });
    return { nodes: data.nodes.filter(n => !hidden.has(n.id) && reachable.has(n.id)), links };
  }, [data, activeAlignments]);

  useEffect(() => {
    if (loading || !svgRef.current || !containerRef.current) return;
    const { nodes: rawNodes, links: rawLinks } = filteredData();
    if (rawNodes.length === 0) { d3.select(svgRef.current).selectAll('*').remove(); return; }

    const W = containerRef.current.clientWidth || 1100;
    const H = 600;
    const margin = { top: 10, right: 160, bottom: 20, left: 20 };

    const nodeMap = {};
    const nodes = rawNodes.map((n, i) => { const o = { ...n, index: i }; nodeMap[n.id] = o; return o; });
    const links = rawLinks.filter(l => nodeMap[l.source] && nodeMap[l.target])
      .map(l => ({ ...l, source: nodeMap[l.source], target: nodeMap[l.target] }));

    const sankeyGen = d3Sankey()
      .nodeId(d => d.id)
      .nodeWidth(20).nodePadding(10)
      .extent([[margin.left, margin.top], [W - margin.right, H - margin.bottom]]);

    let graph;
    try { graph = sankeyGen({ nodes, links }); }
    catch (e) { console.error('Sankey layout error:', e); return; }

    const svg = d3.select(svgRef.current).attr('width', W).attr('height', H);
    svg.selectAll('*').remove();

    const defs = svg.append('defs');
    defs.append('style').text(`
      @keyframes pulse-border { 0%,100%{opacity:1} 50%{opacity:0.3} }
      .node-constrained rect { animation: pulse-border 2s infinite; }
      .node-critical rect { animation: pulse-border 1s infinite; }
    `);
    graph.links.forEach((l, i) => {
      const g = defs.append('linearGradient').attr('id', `sg-${i}`).attr('gradientUnits','userSpaceOnUse')
        .attr('x1', l.source.x1).attr('x2', l.target.x0);
      g.append('stop').attr('offset','0%').attr('stop-color', nodeColor(l.source)).attr('stop-opacity', 0.8);
      g.append('stop').attr('offset','100%').attr('stop-color', nodeColor(l.target)).attr('stop-opacity', 0.8);
    });

    // Downstream traversal
    const downstreamIds = new Set();
    if (simulateMode && selectedComp) {
      const traverse = id => graph.links.forEach(l => {
        const sid = l.source.id; const tid = l.target.id;
        if (sid === id && !downstreamIds.has(tid)) { downstreamIds.add(tid); traverse(tid); }
      });
      downstreamIds.add(selectedComp); traverse(selectedComp);
    }
    const isAffected = id => simulateMode && selectedComp && downstreamIds.has(id);
    const linkAffected = l => simulateMode && selectedComp && (downstreamIds.has(l.source.id) || downstreamIds.has(l.target.id));

    // Links
    svg.append('g').attr('fill','none').selectAll('path').data(graph.links).join('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke', (d, i) => `url(#sg-${i})`)
      .attr('stroke-width', d => Math.max(1, d.width))
      .attr('opacity', d => !simulateMode || !selectedComp ? 0.3 : linkAffected(d) ? 0.7 : 0.06)
      .on('mouseenter', function() { d3.select(this).attr('opacity', 0.65); })
      .on('mouseleave', function(_, d) { d3.select(this).attr('opacity', !simulateMode || !selectedComp ? 0.3 : linkAffected(d) ? 0.7 : 0.06); });

    // Nodes
    const nodeGroups = svg.append('g').selectAll('g').data(graph.nodes).join('g')
      .attr('class', d => d.type === 'component' && d.availability === 'critical_shortage' ? 'node-critical' : d.type === 'component' && d.availability === 'constrained' ? 'node-constrained' : '')
      .attr('transform', d => `translate(${d.x0},${d.y0})`)
      .style('cursor', d => simulateMode && d.type === 'component' ? 'pointer' : 'default')
      .on('mouseenter', (ev, d) => setTooltip({ node: d, pos: { x: ev.clientX, y: ev.clientY } }))
      .on('mousemove',  (ev)    => setTooltip(p => p ? { ...p, pos: { x: ev.clientX, y: ev.clientY } } : p))
      .on('mouseleave', ()      => setTooltip(null))
      .on('click', (_, d) => { if (!simulateMode || d.type !== 'component') return; setSelectedComp(p => p === d.id ? null : d.id); });

    nodeGroups.append('rect')
      .attr('height', d => Math.max(2, d.y1 - d.y0)).attr('width', d => d.x1 - d.x0)
      .attr('fill', d => {
        if (simulateMode && selectedComp) {
          if (d.id === selectedComp) return '#EF4444';
          if (isAffected(d.id)) return d.type === 'growth_coa' ? (COA_COLOR[d.action_type] || '#7C3AED') : '#EF4444';
          return '#1E2D45';
        }
        return nodeColor(d);
      })
      .attr('stroke', d => nodeBorderColor(d))
      .attr('stroke-width', d => ['component','equipment','capability','vertical','growth_coa'].includes(d.type) && nodeBorderColor(d) !== 'none' ? (d.type === 'component' ? 2 : 1.5) : 0)
      .attr('stroke-dasharray', d => d.type === 'vertical' && !d.has_coa ? '4,3' : null)
      .attr('opacity', d => !simulateMode || !selectedComp ? 1 : d.id === selectedComp ? 1 : (d.type === 'growth_coa' && isAffected(d.id)) ? 1 : isAffected(d.id) ? 0.9 : 0.15)
      .attr('rx', 2);

    nodeGroups.append('text')
      .attr('x', d => d.x0 < W / 2 ? (d.x1 - d.x0) + 6 : -6)
      .attr('y', d => (d.y1 - d.y0) / 2).attr('dy', '0.35em')
      .attr('text-anchor', d => d.x0 < W / 2 ? 'start' : 'end')
      .attr('font-size', 11)
      .attr('fill', d => !simulateMode || !selectedComp ? '#CBD5E1' : isAffected(d.id) ? '#F1F5F9' : '#374151')
      .text(d => {
        const lbl = d.type === 'growth_coa' ? `${COA_ICON[d.action_type] || ''} ${d.name}` : d.name;
        return lbl.length > 22 ? lbl.slice(0, 22) + '…' : lbl;
      });

    nodeGroups.filter(d => d.type === 'vertical' && !d.has_coa)
      .append('text')
      .attr('x', d => (d.x1 - d.x0) + 6).attr('y', d => (d.y1 - d.y0) + 12)
      .attr('font-size', 9).attr('fill', '#EF4444').text('⚠ No COA');

  }, [loading, filteredData, simulateMode, selectedComp]);

  const colPositions = useCallback(() => {
    if (!data.nodes.length) return [];
    const W = containerRef.current?.clientWidth || 1100;
    const usableW = W - 20 - 160;
    return COLS.map((col, i) => ({ label: col.label, x: 20 + (usableW / 4) * i }));
  }, [data.nodes.length]);

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '12px 0', borderBottom: '1px solid #1E2D45', marginBottom: 12 }}>
        <button
          onClick={() => { setSimulateMode(m => !m); setSelectedComp(null); }}
          style={{ background: simulateMode ? '#EF4444' : '#1E2D45', color: simulateMode ? '#fff' : '#94A3B8', border: `1px solid ${simulateMode ? '#EF4444' : '#374151'}`, borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
        >
          {simulateMode ? '⚠ Shortage Mode ON' : 'Simulate Shortage'}
        </button>
        {simulateMode && <span style={{ fontSize: 11, color: '#F59E0B' }}>Click a component node to trace downstream impact</span>}
        {selectedComp && (
          <button onClick={() => setSelectedComp(null)} style={{ background: 'transparent', color: '#94A3B8', border: '1px solid #374151', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>
            Reset
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#64748B' }}>Show:</span>
          {ALIGNMENT_FILTERS.map(a => (
            <button key={a} onClick={() => toggleAlignment(a)} style={{
              background: activeAlignments.has(a) ? ALIGNMENT_COLOR[a] + '22' : 'transparent',
              color: activeAlignments.has(a) ? ALIGNMENT_COLOR[a] : '#4B5563',
              border: `1px solid ${activeAlignments.has(a) ? ALIGNMENT_COLOR[a] : '#374151'}`,
              borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 10, fontWeight: 600,
            }}>
              {a.replace('_',' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Column headers */}
      <div style={{ position: 'relative', height: 20, marginBottom: 4 }}>
        {colPositions().map(({ label, x }) => (
          <span key={label} style={{ position: 'absolute', left: x, top: 0, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: '#64748B' }}>{label}</span>
        ))}
      </div>

      {/* SVG */}
      <div ref={containerRef} style={{ width: '100%' }}>
        {loading && <div style={{ height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>Loading supply chain data…</div>}
        {error   && <div style={{ height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EF4444' }}>{error}</div>}
        {!loading && !error && <svg ref={svgRef} style={{ display: 'block', width: '100%' }} />}
      </div>

      {/* Legend */}
      {!loading && !error && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid #1E2D45' }}>
          <span style={{ fontSize: 10, color: '#64748B', fontWeight: 700, alignSelf: 'center' }}>ALIGNMENT:</span>
          {Object.entries(ALIGNMENT_COLOR).map(([k, c]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 10, height: 10, background: c, borderRadius: 2 }} />
              <span style={{ fontSize: 10, color: '#94A3B8' }}>{k.replace('_',' ')}</span>
            </div>
          ))}
          <div style={{ width: 1, background: '#1E2D45', margin: '0 4px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 10, height: 10, background: '#1E2D45', border: '1.5px dashed #EF4444', borderRadius: 2 }} />
            <span style={{ fontSize: 10, color: '#94A3B8' }}>vertical — no COA</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 10, height: 10, background: '#1E2D45', border: '1.5px solid #22D3EE', borderRadius: 2 }} />
            <span style={{ fontSize: 10, color: '#94A3B8' }}>vertical — COA exists</span>
          </div>
        </div>
      )}

      <SankeyTooltip node={tooltip?.node} pos={tooltip?.pos} />
    </div>
  );
}

// ─── ROOT COMPONENT ────────────────────────────────────────────────────────────

export default function SupplyChain() {
  const [topView, setTopView] = useState('sankey');
  const [sankeyData, setSankeyData]     = useState({ nodes: [], links: [] });
  const [sankeyLoading, setSankeyLoading] = useState(true);
  const [sankeyError, setSankeyError]   = useState(null);

  const refreshSankey = useCallback(async () => {
    try {
      setSankeyLoading(true); setSankeyError(null);
      setSankeyData(await crmService.getSankey());
    } catch (err) { setSankeyError(err.message); }
    finally { setSankeyLoading(false); }
  }, []);

  useEffect(() => { refreshSankey(); }, [refreshSankey]);

  return (
    <div style={{ padding: '0 0 32px', fontFamily: 'inherit' }}>
      {/* Top sub-nav: Sankey | Manage */}
      <div style={{ display: 'flex', borderBottom: '1px solid #1E2D45', marginBottom: 0 }}>
        {[['sankey','Sankey'],['manage','Manage']].map(([k, label]) => (
          <button key={k} onClick={() => setTopView(k)} style={{
            padding: '10px 22px', background: 'none', border: 'none',
            borderBottom: topView === k ? '2px solid #22D3EE' : '2px solid transparent',
            color: topView === k ? '#22D3EE' : '#6B7280',
            cursor: 'pointer', fontSize: 13, fontWeight: topView === k ? 600 : 400,
            marginBottom: -1,
          }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ paddingTop: 16 }}>
        {topView === 'sankey' && <SankeyView data={sankeyData} loading={sankeyLoading} error={sankeyError} />}
        {topView === 'manage' && <ManagePanel onRefreshSankey={refreshSankey} />}
      </div>
    </div>
  );
}
